import { DEFAULT_FONT } from './fontConfig';
import { getFormatOrDefault } from './formats';
import {
  getQrMargin,
  getQrTemplateGeometry,
  QR_DEFAULT_ERROR_CORRECTION,
  QR_DESIGN_PAYLOAD,
} from './qrSizing';
import type {
  BarcodeElement,
  ImageElement,
  LabelElement,
  QrElement,
  ShapeElement,
  Template,
  TextElement,
} from './types';

// Legacy/migrated/corrupted template JSON is not guaranteed to match the TS types at runtime,
// so string-typed element properties must be coerced defensively before any string operation
// (e.g. .trim/.toUpperCase) is applied against them during rendering.
const toSafeString = <T extends string | undefined>(value: unknown, fallback: T): string | T =>
  typeof value === 'string' ? value : fallback;

const normalizeDpi = (value: unknown, outputFormat: Template['outputFormat']): number => {
  const parsedDpi = Number(value);
  return Number.isFinite(parsedDpi) && parsedDpi > 0
    ? parsedDpi
    : getFormatOrDefault(outputFormat).defaultDpi;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const parseNumericWeight = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (normalized === 'bold') {
    return 700;
  }
  if (normalized === 'normal') {
    return 400;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolveTextFontWeight = (element: TextElement & Record<string, unknown>): number => {
  const explicitWeight = parseNumericWeight(element.fontWeight);
  if (explicitWeight !== null) {
    return explicitWeight;
  }

  const legacyWeight = parseNumericWeight(element['font-weight']);
  if (legacyWeight !== null) {
    return legacyWeight;
  }

  const nestedStyleWeight = parseNumericWeight(
    typeof element.style === 'object' && element.style !== null
      ? (element.style as Record<string, unknown>).fontWeight
      : undefined,
  );
  if (nestedStyleWeight !== null) {
    return nestedStyleWeight;
  }

  if (typeof element.fontStyle === 'string' && element.fontStyle.toLowerCase().includes('bold')) {
    return 700;
  }

  if (element.bold === true) {
    return 700;
  }

  return 400;
};

const NUMBER_PROPERTIES_BY_ELEMENT_TYPE: Record<LabelElement['type'], string[]> = {
  text: ['x', 'y', 'zIndex', 'rotation', 'width', 'fontSize', 'letterSpacing', 'lineHeight', 'scaleX', 'scaleY', 'numberDecimalPlaces'],
  image: ['x', 'y', 'zIndex', 'rotation', 'width', 'height', 'opacity'],
  qr: ['x', 'y', 'zIndex', 'rotation', 'width', 'height', 'margin', 'imageWidth'],
  barcode: ['x', 'y', 'zIndex', 'rotation', 'height', 'margin', 'barWidth'],
  shape: [
    'x', 'y', 'zIndex', 'rotation', 'width', 'height', 'strokeWidth',
    'v1.x', 'v1.y', 'v2.x', 'v2.y', 'v3.x', 'v3.y', 'v4.x', 'v4.y',
  ],
};

// Template JSON can contain unknown types. Never treat inherited Object keys
// (such as "constructor") or coerced non-string values as supported types.
const getElementProperties = (
  propertiesByType: Record<LabelElement['type'], string[]>,
  type: unknown,
): string[] | undefined => {
  if (typeof type !== 'string' || !Object.prototype.hasOwnProperty.call(propertiesByType, type)) {
    return undefined;
  }
  return propertiesByType[type as LabelElement['type']];
};

const NUMBER_DEFAULTS: Record<string, number> = {
  x: 0,
  y: 0,
  zIndex: 0,
  rotation: 0,
  width: 1,
  height: 1,
  fontSize: 12,
  letterSpacing: 0,
  lineHeight: 1,
  opacity: 1,
  margin: 0,
  imageWidth: 1,
  barWidth: 1,
  strokeWidth: 1,
  scaleX: 1,
  scaleY: 1,
  numberDecimalPlaces: 0,
  'v1.x': 0,
  'v1.y': 0,
  'v2.x': 0,
  'v2.y': 0,
  'v3.x': 0,
  'v3.y': 0,
  'v4.x': 0,
  'v4.y': 0,
};

// Match barcodeGenerator's established output defaults for malformed sizing.
// Missing legacy sizing stays unset so the renderer remains authoritative.
const BARCODE_NUMBER_DEFAULTS: Record<string, number> = { height: 50, barWidth: 2, margin: 10 };

const getNestedProperty = (element: Record<string, unknown>, propertyName: string): unknown => {
  return propertyName.split('.').reduce<unknown>((current, segment) => {
    if (!current || typeof current !== 'object' || Array.isArray(current)) return undefined;
    return (current as Record<string, unknown>)[segment];
  }, element);
};

const normalizeNumericProperties = (element: LabelElement): LabelElement => {
  const normalizedElement = { ...element } as LabelElement & Record<string, unknown>;
  const propertyNames = getElementProperties(NUMBER_PROPERTIES_BY_ELEMENT_TYPE, element.type) ?? [];
  for (const propertyName of propertyNames) {
    const value = getNestedProperty(normalizedElement, propertyName);
    // Unset precision is valid and uses the editor/renderer default. Do not turn
    // existing two-decimal labels into explicitly zero-decimal labels on load.
    if (propertyName === 'numberDecimalPlaces' && (value === undefined || value === null)) {
      continue;
    }
    const barcodeDefault = element.type === 'barcode' ? BARCODE_NUMBER_DEFAULTS[propertyName] : undefined;
    if (barcodeDefault !== undefined && (value === undefined || value === null)) continue;
    if (!isFiniteNumber(value)) {
      const segments = propertyName.split('.');
      if (segments.length === 1) {
        normalizedElement[propertyName] = barcodeDefault ?? NUMBER_DEFAULTS[propertyName];
        continue;
      }

      const parent = normalizedElement[segments[0]];
      if (parent && typeof parent === 'object' && !Array.isArray(parent)) {
        normalizedElement[segments[0]] = {
          ...(parent as Record<string, unknown>),
          [segments[1]]: NUMBER_DEFAULTS[propertyName],
        };
      }
    }
  }
  return normalizedElement as LabelElement;
};

const normalizeElement = (element: LabelElement, dpi: number, outputFormat?: Template['outputFormat']): LabelElement => {
  element = normalizeNumericProperties(element);

  if (element.type === 'qr') {
    const qr = element as QrElement;
    const data = toSafeString(qr.data, '');
    const width = Number.isFinite(qr.width) && qr.width > 0 ? Math.round(qr.width) : 1;
    const height = Number.isFinite(qr.height) && qr.height > 0 ? Math.round(qr.height) : width;
    const margin = Number.isFinite(qr.margin)
      ? Math.max(0, outputFormat === 'zpl' ? Math.ceil(qr.margin) : qr.margin)
      : 0;
    const errorCorrectionLevel = qr.errorCorrectionLevel === 'L'
      || qr.errorCorrectionLevel === 'M'
      || qr.errorCorrectionLevel === 'Q'
      || qr.errorCorrectionLevel === 'H'
      ? qr.errorCorrectionLevel
      : QR_DEFAULT_ERROR_CORRECTION;
    const requestedSize = Math.min(width, height);
    const size = getQrTemplateGeometry({
      value: data || QR_DESIGN_PAYLOAD,
      requestedSize,
      dpi,
      outputFormat,
      margin: 0,
      errorCorrectionLevel,
    }).size;
    return {
      ...qr,
      data,
      fg: toSafeString(qr.fg, '#000000'),
      bg: toSafeString(qr.bg, '#ffffff'),
      width: size,
      height: size,
      margin: Math.min(margin, getQrMargin({ value: data || QR_DESIGN_PAYLOAD,
        requestedSize: size, dpi, outputFormat, errorCorrectionLevel }).maximum),
      errorCorrectionLevel,
    };
  }
  if (element.type === 'image') {
    const img = element as ImageElement;
    return {
      ...img,
      url: toSafeString(img.url, ''),
      alt: toSafeString(img.alt, undefined),
      fit: toSafeString(img.fit, 'contain') as ImageElement['fit'],
    } as ImageElement;
  }
  if (element.type === 'barcode') {
    const bc = element as BarcodeElement;
    return {
      ...bc,
      data: toSafeString(bc.data, ''),
      dataSource: toSafeString(bc.dataSource, 'static') as BarcodeElement['dataSource'],
      format: toSafeString(bc.format, 'CODE128') as BarcodeElement['format'],
      lineColor: toSafeString(bc.lineColor, '#000000'),
      background: toSafeString(bc.background, '#ffffff'),
    } as BarcodeElement;
  }
  if (element.type === 'shape') {
    const shape = element as ShapeElement;
    return {
      ...shape,
      shapeType: toSafeString(shape.shapeType, 'rectangle') as ShapeElement['shapeType'],
      strokeColor: toSafeString(shape.strokeColor, '#000000'),
      fillColor: toSafeString(shape.fillColor, undefined),
      svgContent: toSafeString(shape.svgContent, undefined),
    } as ShapeElement;
  }
  if (element.type !== 'text') {
    return element;
  }

  const textElement = element as TextElement & Record<string, unknown>;
  return {
    ...textElement,
    text: toSafeString(textElement.text, ''),
    variableKey: toSafeString(textElement.variableKey, undefined),
    dateFormat: toSafeString(textElement.dateFormat, undefined),
    color: toSafeString(textElement.color, '#000000'),
    align: toSafeString(textElement.align, 'left') as TextElement['align'],
    textTransform: toSafeString(textElement.textTransform, 'none') as TextElement['textTransform'],
    fontFamily: toSafeString(textElement.fontFamily, DEFAULT_FONT),
    fontWeight: resolveTextFontWeight(textElement),
    ...(textElement.fontStyle !== undefined ? {
      fontStyle: typeof textElement.fontStyle === 'string'
        && textElement.fontStyle.toLowerCase().includes('italic') ? 'italic' : 'normal',
    } : {}),
  } as TextElement;
};

export const normalizeLoadedTemplate = (template: Template): Template => {
  const dpi = normalizeDpi(template.dpi, template.outputFormat);

  return {
    ...template,
    dpi,
    elements: (Array.isArray(template.elements) ? template.elements : [])
      .filter((element): element is LabelElement => Boolean(element && typeof element === 'object'))
      .map((element) => normalizeElement(element, dpi, template.outputFormat)),
  };
};

// String-typed properties per element type that must be strings when present.
// Kept in sync with the string coercions applied in `normalizeElement` above.
const STRING_PROPERTIES_BY_ELEMENT_TYPE: Record<LabelElement['type'], string[]> = {
  text: ['text', 'variableKey', 'dateFormat', 'fontFamily', 'color', 'align', 'textTransform'],
  image: ['url', 'alt'],
  qr: ['data', 'fg', 'bg', 'errorCorrectionLevel'],
  barcode: ['data', 'dataSource', 'format', 'lineColor', 'background'],
  shape: ['shapeType', 'strokeColor', 'fillColor', 'svgContent'],
};

/**
 * Validates that an already-parsed template's elements have string-typed values for
 * properties that require strings, before the template is sent to the server for persistence.
 * Returns a list of actionable, human-readable error messages (empty when valid).
 */
export const normalizeQrMarginsForSave = (template: Template): Template => ({
  ...template,
  elements: template.elements.map(element => {
    if (element.type !== 'qr') return element;
    const minimum = getQrMargin({
      value: element.data || QR_DESIGN_PAYLOAD,
      requestedSize: Math.min(element.width, element.height),
      dpi: template.dpi ?? 72,
      outputFormat: template.outputFormat,
      errorCorrectionLevel: element.errorCorrectionLevel,
    }).minimum;
    return { ...element, margin: Math.max(element.margin ?? 0, minimum) };
  }),
});

export const validateTemplateForSave = (template: Template, enforceQrMargins = false): string[] => {
  const errors: string[] = [];
  const elements = Array.isArray(template.elements) ? template.elements : [];

  elements.forEach((element, index) => {
    if (!element || typeof element !== 'object') {
      errors.push(`Element at position ${index + 1} must be an object.`);
      return;
    }

    const label = element?.id ? `Element "${element.id}"` : `Element at position ${index + 1}`;
    const stringProperties = getElementProperties(STRING_PROPERTIES_BY_ELEMENT_TYPE, element.type);
    const numberProperties = getElementProperties(NUMBER_PROPERTIES_BY_ELEMENT_TYPE, element.type);
    if (!stringProperties || !numberProperties) {
      errors.push(`${label}: unsupported element type.`);
      return;
    }

    for (const propertyName of stringProperties) {
      const value = (element as unknown as Record<string, unknown>)[propertyName];
      if (value === undefined || value === null) {
        continue;
      }
      if (typeof value !== 'string') {
        const valueType = Array.isArray(value) ? 'array' : typeof value;
        errors.push(`${label}: property "${propertyName}" must be text, but received ${valueType}.`);
      }
    }

    for (const propertyName of numberProperties) {
      const value = getNestedProperty(element as unknown as Record<string, unknown>, propertyName);
      if (value === undefined || value === null) {
        continue;
      }
      if (!isFiniteNumber(value)) {
        const valueType = Array.isArray(value) ? 'array' : typeof value;
        errors.push(`${label}: property "${propertyName}" must be a finite number, but received ${valueType}.`);
      }
    }
  });

  if (enforceQrMargins && errors.length === 0) {
    const normalized = normalizeQrMarginsForSave(template);
    elements.forEach((element, index) => {
      if (element.type === 'qr' && (element.margin ?? 0) + 1e-9 < (normalized.elements[index] as QrElement).margin) {
        errors.push(`Element "${element.id}": QR margin must meet the minimum for its size.`);
      }
    });
  }
  return errors;
};
